import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type TextareaHTMLAttributes,
  type ReactNode,
} from 'react';
import { Select, type SelectProps } from './select';
export { Select } from './select';

export function Field({
  className = '',
  htmlFor,
  children,
  ...props
}: LabelHTMLAttributes<HTMLLabelElement>): JSX.Element {
  return (
    <label {...props} htmlFor={htmlFor} className={`ui-field ${className}`.trim()}>
      {children}
    </label>
  );
}
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className = '', type = 'text', ...props }, ref) {
    return (
      <input
        {...props}
        type={type}
        ref={ref}
        className={`${type === 'checkbox' || type === 'radio' ? 'ui-checkbox' : 'ui-input'} ${className}`.trim()}
      />
    );
  },
);
export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { autoResize?: boolean }
>(function Textarea({ className = '', autoResize = false, value, defaultValue, ...props }, ref) {
  const element = useRef<HTMLTextAreaElement | null>(null);
  const setRef = useCallback(
    (node: HTMLTextAreaElement | null): void => {
      element.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );
  const resize = useCallback((): void => {
    const node = element.current;
    if (!node || !autoResize || node.clientWidth === 0) return;
    const style = getComputedStyle(node);
    const borders = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    node.style.height = 'auto';
    node.style.height = `${node.scrollHeight + borders}px`;
  }, [autoResize]);
  useLayoutEffect(resize, [resize, value, defaultValue]);
  useEffect(() => {
    const node = element.current;
    if (!node || !autoResize) return;
    let width = node.getBoundingClientRect().width;
    const observer = new ResizeObserver(() => {
      const nextWidth = node.getBoundingClientRect().width;
      if (nextWidth === width) return;
      width = nextWidth;
      resize();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [autoResize, resize]);
  return (
    <textarea
      {...props}
      value={value}
      defaultValue={defaultValue}
      ref={setRef}
      onInput={(event) => {
        props.onInput?.(event);
        resize();
      }}
      className={`ui-input ui-textarea${autoResize ? ' ui-textarea--auto-resize' : ''} ${className}`.trim()}
    />
  );
});
type Details = { label: ReactNode; hint?: ReactNode; error?: ReactNode; fieldClassName?: string };
export const TextField = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & Details
>(function TextField({ label, hint, error, id, fieldClassName = '', ...props }, ref): JSX.Element {
  const [nativeError, setNativeError] = useState('');
  const displayedError = error ?? nativeError;
  const generated = useId();
  const fieldId = id ?? generated;
  const description =
    [
      props['aria-describedby'],
      hint ? `${fieldId}-hint` : '',
      displayedError ? `${fieldId}-error` : '',
    ]
      .filter(Boolean)
      .join(' ') || undefined;
  return (
    <div className={`ui-field ${fieldClassName}`.trim()}>
      <Field htmlFor={fieldId}>{label}</Field>
      <Input
        {...props}
        ref={ref}
        onChange={(event) => {
          setNativeError('');
          props.onChange?.(event);
        }}
        onInvalid={(event) => {
          props.onInvalid?.(event);
          if (event.defaultPrevented) return;
          event.preventDefault();
          setNativeError(event.currentTarget.validationMessage);
          const firstInvalid = Array.from(event.currentTarget.form?.elements ?? []).find(
            (element) =>
              (element instanceof HTMLInputElement ||
                element instanceof HTMLSelectElement ||
                element instanceof HTMLTextAreaElement) &&
              element.willValidate &&
              !element.validity.valid,
          );
          if (firstInvalid === event.currentTarget) event.currentTarget.focus();
        }}
        id={fieldId}
        aria-describedby={description}
        aria-invalid={displayedError ? true : props['aria-invalid']}
      />
      {hint && (
        <span id={`${fieldId}-hint`} className="ui-field-hint">
          {hint}
        </span>
      )}
      {displayedError && (
        <span role="alert" id={`${fieldId}-error`} className="ui-field-error">
          {displayedError}
        </span>
      )}
    </div>
  );
});

export function SelectField({
  label,
  hint,
  error,
  id,
  fieldClassName = '',
  ...props
}: SelectProps & Details): JSX.Element {
  const generated = useId();
  const fieldId = id ?? generated;
  const description =
    [props['aria-describedby'], hint ? `${fieldId}-hint` : '', error ? `${fieldId}-error` : '']
      .filter(Boolean)
      .join(' ') || undefined;
  return (
    <div className={`ui-field ${fieldClassName}`.trim()}>
      <Field htmlFor={fieldId}>{label}</Field>
      <Select
        {...props}
        id={fieldId}
        aria-describedby={description}
        aria-invalid={error ? true : props['aria-invalid']}
      />
      {hint && (
        <span id={`${fieldId}-hint`} className="ui-field-hint">
          {hint}
        </span>
      )}
      {error && (
        <span role="alert" id={`${fieldId}-error`} className="ui-field-error">
          {error}
        </span>
      )}
    </div>
  );
}
